////////////////////////////////////////////
// FUZE LOG — CLIENT LOGGING + SESSION CORRELATION (PORT OF web/app/log.js)
////////////////////////////////////////////
//
// ONE LEVELED LOGGER THAT MIRRORS TO THE CONSOLE AND SHIPS A BATCHED COPY TO
// POST /api/log SO A WHOLE USER SESSION IS TRACEABLE END-TO-END IN THE ADMIN
// LOGS/SESSIONS DASHBOARDS. THE CORRELATION MODEL (DO NOT BREAK):
//
//   sid  — PERSISTENT PER BROWSER (localStorage fuze:sid) -> X-Fuze-Session
//   plid — ONE PER "PAGE LOAD"; IN THIS SPA, ONE PER ROUTE NAVIGATION
//          (newPageLoad() IS CALLED BY THE ROUTER SUBSCRIBER) -> X-Fuze-Page
//   aid  — ONE PER USER ACTION (COMMIT/CREATE/CLAIM SPANS BUILD+SUBMIT)
//          -> X-Fuze-Action
//
// LIKE THE VANILLA APP, window.fetch IS PATCHED SO EVERY SAME-ORIGIN REQUEST
// CARRIES THE HEADERS NO MATTER WHICH CODE PATH ISSUED IT. LOGGING MUST
// NEVER THROW OR RECURSE (SHIPPING USES THE CAPTURED ORIGINAL FETCH).

// CAPTURE ORIGINAL FETCH BEFORE PATCHING — SHIPPING NEVER RECURSES
const ORIGIN_FETCH = window.fetch ? window.fetch.bind(window) : null;

type Level = "debug" | "info" | "warn" | "error";
const RANK: Record<Level, number> = { debug: 10, info: 20, warn: 30, error: 40 };

// LOG META — ARBITRARY STRUCTURED CONTEXT ON A LINE
type Meta = Record<string, unknown> | string | number | null | undefined;

interface Entry {
  lvl: Level;
  ctx: string;
  msg: string;
  ts: number;
  plid: string;
  page: string;
  url: string;
  aid?: string;
  net?: string;
  meta?: Record<string, unknown>;
}

function safeStore(op: "get" | "set", key: string, val?: string): string | null {

  try {
    if (op === "get") return localStorage.getItem(key);
    localStorage.setItem(key, val!);
    return null;
  } catch {
    return null;
  }
}

// ID HELPERS — crypto.randomUUID ON SECURE CONTEXTS, ELSE A FALLBACK
function uuid(): string {

  if (window.crypto && crypto.randomUUID) return crypto.randomUUID();
  return "x" + Date.now().toString(36) + Math.random().toString(36).slice(2, 12);
}
const shortId = () => uuid().replace(/-/g, "").slice(0, 12);

// SESSION ID — ONE PER BROWSER, PERSISTED
function loadSid(): string {

  let s = safeStore("get", "fuze:sid");
  if (!s) {

    s = uuid();
    safeStore("set", "fuze:sid", s);
  }
  return s;
}
const SID = loadSid();

// LEVEL — ?log= WINS, THEN STORED PREFERENCE, THEN devnet=debug / info
const params = new URLSearchParams(location.search);
const LEVEL = (params.get("log") || safeStore("get", "fuze:loglevel") ||
  (params.get("net") === "devnet" ? "debug" : "info")) as Level;
const THRESHOLD = RANK[LEVEL] || RANK.info;

// NET — STAMPED ON EVERY LINE (RE-READ PER NAVIGATION VIA newPageLoad)
let currentNet: string | null = params.get("net") || null;

// PAGE-LOAD ID — ONE PER ROUTE NAVIGATION IN THE SPA
let PLID = shortId();

// CURRENT ACTION — AMBIENT aid; THE UI RUNS ONE USER ACTION AT A TIME
let currentAid: string | null = null;

export function beginAction(name: string): string {

  currentAid = shortId();
  debug("action", "start", { action: name });
  return currentAid;
}
export const endAction = () => { currentAid = null; };

////////////////////////////////////////////
// BUFFER + SHIP
////////////////////////////////////////////
const buffer: Entry[] = [];
const MAX_BUFFER = 200;
const BATCH_AT = 20;
const FLUSH_MS = 5000;
let timer: ReturnType<typeof setTimeout> | null = null;

function enqueue(entry: Entry, lvl: Level) {

  buffer.push(entry);
  // DROP OLDEST — A DEAD ENDPOINT CAN NEVER GROW MEMORY UNBOUNDED
  if (buffer.length > MAX_BUFFER)
    buffer.shift();
  if (lvl === "error" || buffer.length >= BATCH_AT) flush();
  else schedule();
}

function schedule() {

  if (timer) return;
  timer = setTimeout(() => { timer = null; flush(); }, FLUSH_MS);
}

export function flush(useBeacon = false) {

  if (!buffer.length) return;

  const batch = buffer.splice(0, buffer.length);
  const payload = JSON.stringify({ sid: SID, entries: batch });
  try {
    // PAGE UNLOAD — sendBeacon SO THE LAST LINES SURVIVE NAVIGATION
    if (useBeacon && navigator.sendBeacon) {

      navigator.sendBeacon("/api/log", new Blob([payload], { type: "application/json" }));
      return;
    }
    if (ORIGIN_FETCH)
      ORIGIN_FETCH("/api/log", { method: "POST", headers: { "content-type": "application/json" }, body: payload, keepalive: true })
        .catch(() => { /* SHIP FAILURE IS SWALLOWED — NEVER RE-LOG */ });
  } catch {
    // LOGGING MUST NEVER THROW
  }
}

////////////////////////////////////////////
// CORE
////////////////////////////////////////////
function mirror(lvl: Level, ctx: string, msg: string, meta: Meta) {

  const fn = console[lvl] || console.log;
  try {
    fn.call(console, "[" + ctx + "] " + msg, meta == null ? "" : meta);
  } catch {
    // CONSOLE FAILURE IS NON-FATAL
  }
}

function record(lvl: Level, ctx: string, msg: string, meta?: Meta) {

  if (RANK[lvl] < THRESHOLD) return;

  ctx = String(ctx ?? "app").slice(0, 64);
  msg = String(msg ?? "").slice(0, 2000);
  mirror(lvl, ctx, msg, meta);

  const entry: Entry = {
    lvl, ctx, msg, ts: Date.now(),
    plid: PLID,
    page: location.pathname.slice(0, 128),
    url: (location.pathname + location.search).slice(0, 512),
  };
  if (currentAid) entry.aid = currentAid;
  if (currentNet) entry.net = currentNet;
  if (meta != null) entry.meta = typeof meta === "object" ? meta : { value: meta };
  enqueue(entry, lvl);
}

export const debug = (ctx: string, msg: string, meta?: Meta) => record("debug", ctx, msg, meta);
export const info = (ctx: string, msg: string, meta?: Meta) => record("info", ctx, msg, meta);
export const warn = (ctx: string, msg: string, meta?: Meta) => record("warn", ctx, msg, meta);
export const error = (ctx: string, msg: string, meta?: Meta) => record("error", ctx, msg, meta);
export const sessionId = () => SID;

////////////////////////////////////////////
// FETCH CORRELATION — SAME-ORIGIN REQUESTS GET THE HEADERS + A BREADCRUMB
////////////////////////////////////////////
function isSameOrigin(u: string): boolean {

  try {
    return new URL(u, location.href).origin === location.origin;
  } catch {
    return u.charAt(0) === "/";
  }
}
function pathOf(u: string): string {

  try {
    return new URL(u, location.href).pathname;
  } catch {
    return u.split("?")[0];
  }
}

declare global {
  interface Window { __fuzeFetchPatched?: boolean }
}

function patchFetch() {

  if (!window.fetch || window.__fuzeFetchPatched) return;

  window.__fuzeFetchPatched = true;
  const orig = window.fetch.bind(window);
  window.fetch = function (input: RequestInfo | URL, init?: RequestInit) {

    try {
      const isStr = typeof input === "string" || input instanceof URL;
      const url = isStr ? String(input) : null;
      if (isStr && url && isSameOrigin(url) && url.indexOf("/api/log") === -1) {

        const headers = new Headers(init?.headers || {});
        headers.set("X-Fuze-Session", SID);
        headers.set("X-Fuze-Page", PLID);
        if (currentAid) headers.set("X-Fuze-Action", currentAid);
        init = Object.assign({}, init, { headers });

        const started = Date.now();
        const aidAtCall = currentAid;
        const method = (init.method || "GET").toUpperCase();
        return orig(input, init).then(
          (res) => {

            debug("fetch", method + " " + pathOf(url), { status: res.status, ms: Date.now() - started, rid: res.headers?.get("X-Request-Id"), aid: aidAtCall });
            return res;
          },
          (err) => {

            warn("fetch", "request failed: " + pathOf(url), { err: String(err?.message || err), aid: aidAtCall });
            throw err;
          }
        );
      }
    } catch {
      // FALL THROUGH — A PATCH ERROR MUST NEVER BREAK THE APP'S FETCH
    }
    return orig(input, init);
  };
}

////////////////////////////////////////////
// GLOBAL ERROR NET + UNLOAD FLUSH
////////////////////////////////////////////
function installGlobalHandlers() {

  window.addEventListener("error", (e) => {

    const msg = e?.message || e?.error?.message || "script error";
    error("uncaught", msg, { stack: e?.error?.stack, src: e?.filename, line: e?.lineno, col: e?.colno });
  });
  window.addEventListener("unhandledrejection", (e) => {

    const r = e?.reason;
    error("unhandledrejection", r?.message || String(r), { stack: r?.stack });
  });
  window.addEventListener("visibilitychange", () => {

    if (document.visibilityState === "hidden") flush(true);
  });
  window.addEventListener("pagehide", () => flush(true));
}

////////////////////////////////////////////
// SESSION/TRAFFIC BEACON — COUNTS PASSIVE VISITORS FOR THE ADMIN DASHBOARD
////////////////////////////////////////////
//
// getWallet IS INJECTED SO THIS MODULE NEVER IMPORTS WALLET CODE (WALLET
// MODULES CALL pingSession THEMSELVES AFTER CONNECT).
let getWallet: () => string | null = () => null;
export function setWalletGetter(fn: () => string | null) { getWallet = fn; }

export function pingSession() {

  try {
    // EXTERNAL REFERRER HOST ONLY — SAME-ORIGIN/DIRECT VISITS CARRY NONE
    let ref = "";
    try {
      if (document.referrer) {

        const rh = new URL(document.referrer).hostname;
        if (rh && rh !== location.hostname) ref = rh;
      }
    } catch { /* MALFORMED REFERRER — NO SOURCE */ }

    const w = getWallet();
    const payload: Record<string, unknown> = {
      sid: SID,
      page: (location.pathname + location.search).slice(0, 200),
      ref,
      lang: navigator.language || "",
      screen: window.screen?.width && screen.height ? screen.width + "x" + screen.height : "",
    };
    if (currentNet) payload.net = currentNet;
    if (w) payload.wallet = w;

    if (ORIGIN_FETCH)
      ORIGIN_FETCH("/api/session", { method: "POST", headers: { "content-type": "application/json", "X-Fuze-Page": PLID }, body: JSON.stringify(payload), keepalive: true })
        .catch(() => { /* TELEMETRY NEVER SURFACES */ });
  } catch {
    // TELEMETRY MUST NEVER THROW
  }
}

////////////////////////////////////////////
// PAGE-LOAD LINE — ONE PER ROUTE NAVIGATION (SPA EQUIVALENT OF A DOC LOAD)
////////////////////////////////////////////
function pageView() {

  try {
    let ref = "";
    try {
      if (document.referrer) {

        const rh = new URL(document.referrer).hostname;
        if (rh && rh !== location.hostname) ref = rh;
      }
    } catch { /* IGNORE */ }
    info("pageview", "page load", ref ? { ref } : undefined);
    // SHIP NOW SO THE PAGE-LOAD HEADER LANDS WITH ITS REQUESTS
    flush();
  } catch {
    // TELEMETRY MUST NEVER THROW
  }
}

// ROUTE NAVIGATION — MINT A FRESH plid AND RE-BEACON, CALLED BY THE ROUTER
// SUBSCRIBER IN PageShell. THE FIRST (REAL) DOCUMENT LOAD IS HANDLED BY arm().
export function newPageLoad(net: string | null) {

  PLID = shortId();
  currentNet = net;
  pageView();
  pingSession();
}

// ARM — PATCH FETCH + HANDLERS BEFORE ANY APP CODE FETCHES, THEN EMIT THE
// FIRST PAGE-LOAD LINE + SESSION BEACON. CALLED ONCE FROM main.tsx.
let armed = false;
export function arm() {

  if (armed) return;

  armed = true;
  patchFetch();
  installGlobalHandlers();
  pageView();
  pingSession();
}
